import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetInactiveUsersByTypeQuery, useToggleUserStatusMutation } from '../services/auth/authApi';
import { toast } from 'react-toastify';
import { fetchParentShare, clearParentShare } from '../store/actions/userManagement';
import { formatDisplayUserId } from '../utils/userIdFormat';
import PartnershipModal from '../components/PartnershipModal';
import PasswordModal from '../components/PasswordModal';

// User type mappings
const USER_TYPE_MAP = {
  'super-admin': { value: 2, label: 'Dead Super Admin', apiType: 'SuperAdmin', typeKey: 'superadmin' },
  'mini-admin': { value: 3, label: 'Dead Mini Admin', apiType: 'MiniAdmin', typeKey: 'miniadmin' },
  'master-agent': { value: 4, label: 'Dead Master', apiType: 'Master', typeKey: 'master' },
  'super-agent': { value: 5, label: 'Dead Super Agent', apiType: 'Super', typeKey: 'supermaster' },
  'agent': { value: 6, label: 'Dead Agent', apiType: 'Agent', typeKey: 'agent' },
  'client': { value: 7, label: 'Dead Client', apiType: 'Client', typeKey: 'client' },
};

// Icons
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

const DeadClientPage = () => {
  const navigate = useNavigate();
  const { userType: userTypeSlug } = useParams();
  const [search, setSearch]         = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [partnershipModal, setPartnershipModal] = useState({
    isOpen: false,
    userId: null,
  });
  const [passwordModal, setPasswordModal] = useState({
    isOpen: false,
    user: null,
  });

  // Get parent share from Redux
  const { parentShare, loading } = useSelector(function (state) {
    return state.userManagement;
  });

  // Get selected user type from URL param
  const selectedUserTypeObj = USER_TYPE_MAP[userTypeSlug];
  const selectedLabel = selectedUserTypeObj?.label || 'Dead Client';
  const apiUserType = selectedUserTypeObj?.apiType;

  // Fetch inactive users using RTK Query
  const { data, isLoading, error, refetch } = useGetInactiveUsersByTypeQuery(
    { userType: apiUserType, search: appliedSearch },
    { skip: !apiUserType }
  );

  // Toggle user status mutation
  const [toggleUserStatus, { isLoading: isToggling }] = useToggleUserStatusMutation();

  const users = data?.data || [];

  // Handle partnership click
  const handlePartnershipClick = function (userId) {
    setPartnershipModal({ isOpen: true, userId: userId });
    fetchParentShare(userId);
  };

  const handleClosePartnership = function () {
    setPartnershipModal({ isOpen: false, userId: null });
    clearParentShare();
  };

  const formatUserId = function (userId) {
    return formatDisplayUserId(userId);
  };

  // Handle dropdown toggle
  const handleDropdownClick = function(e, userId) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setActiveDropdown(activeDropdown === userId ? null : userId);
  };

  // Close dropdown when clicking outside
  useEffect(function() {
    const handleClickOutside = function(event) {
      // Don't close if clicking inside the dropdown
      if (event.target.closest('ul.dropdown-menu')) {
        return;
      }
      setActiveDropdown(null);
    };
    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return function() {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeDropdown]);

  // Handle actions
  const handleAction = async function(action, user) {
    setActiveDropdown(null);

    switch (action) {
      case 'toggleStatus':
        // Activate the inactive user
        try {
          const userId = user.id || user._id;
          await toggleUserStatus(userId).unwrap();
          toast.success('User activated successfully!');
          refetch(); // Refetch the list
        } catch (err) {
          toast.error(err?.data?.message || 'Failed to activate user');
        }
        break;
      case 'statement':
        const statementUserId = user.id || user._id || user.userId;
        navigate('/admin-details/statement/' + statementUserId);
        break;
      case 'accountOps':
        const accountOpsUserId = user.id || user._id;
        navigate('/admin-details/account-operations/' + accountOpsUserId);
        break;
      case 'loginReport':
        const loginReportUserId = user.id || user._id;
        navigate('/admin-details/login-report/' + loginReportUserId);
        break;
      case 'resetPassword':
        setPasswordModal({ isOpen: true, user: user });
        break;
      case 'downline':
        // Navigate to downline (lower level users)
        const userTypeObj = {
          "Admin":"super admin",
          "SuperAdmin":"mini admin",
          "MiniAdmin":"master",
          "Master":"super master",
          "Super":"agent",
          "Agent":"client"
        };
        const adminId = user.id || user._id || user.userId;
        if (adminId) {
          const route = `/admin-details/downline-by-id/${adminId}/${selectedUserTypeObj.value}/${encodeURIComponent(userTypeObj[user.userType])}`;
          navigate(route);
        } else {
          // Fallback to old navigation if no adminId
          navigate(
            `/admin-details/downline/${user.userId}/${selectedUserTypeObj.value}/${encodeURIComponent(user.username)}`,
          );
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white shadow">
        {/* Header */}
        <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between">
          <span className="text-xl text-white font-normal capitalize">
            {selectedLabel}
          </span>
          <button
            onClick={function () {
              navigate(-1);
            }}
            className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
          >
            Back
          </button>
        </div>

        {/* Search Bar */}
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setAppliedSearch(search); } }}
            placeholder="Search by name, username..."
            className="border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400 rounded w-64"
          />
          <button
            onClick={() => setAppliedSearch(search)}
            className="bg-[#0F2327] hover:bg-[#1a3a40] text-white px-4 py-1.5 text-sm rounded transition-colors"
          >
            Search
          </button>
          {appliedSearch && (
            <button
              onClick={() => { setSearch(''); setAppliedSearch(''); }}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 text-sm rounded transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="p-4 text-red-500 text-center">
              {error?.data?.message || 'Failed to load users'}
            </div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0F2327] text-white border-y">
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">#</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Action</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">User</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Name</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Parent</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Password</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Share</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center" colSpan="4">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Commission %</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Coins</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-left" rowSpan="2">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Status</span>
                      </div>
                    </span>
                  </th>
                </tr>
                <tr className="bg-[#0F2327] text-white border-y">
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Match</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Session</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Casino</span>
                      </div>
                    </span>
                  </th>
                  <th className="p-2 border text-center">
                    <span className="ant-table-header-column">
                      <div>
                        <span className="ant-table-column-title">Matka</span>
                      </div>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="p-8 text-center text-gray-500">
                      No data
                    </td>
                  </tr>
                ) : (
                  users.map(function (user, index) {
                    const rowBgColor = index % 2 === 0 ? 'var(--row-even)' : 'var(--row-odd)';
                    return (
                      <tr key={user.userId || index} style={{ backgroundColor: rowBgColor }}>
                        <td className="p-2 border">
                          <button
                            onClick={function () {
                              handlePartnershipClick(user.userId);
                            }}
                            className="bg-blue-500 text-white p-1 rounded"
                            title="View Partnership"
                          >
                            <PlusIcon />
                          </button>
                        </td>
                        <td className="p-2 border">
                          <button
                            onClick={function(e) {
                              handleDropdownClick(e, user.userId);
                            }}
                            className="bg-blue-500 text-white p-1 rounded"
                          >
                            <MenuIcon />
                          </button>
                        </td>
                        <td className="p-2 border text-xs">
                          <a
                            href="#"
                            className="text-blue-600 hover:text-blue-800 hover:underline text-xs flex items-center justify-start"
                            onClick={function (e) {
                              e.preventDefault();
                              handleAction('downline', user);
                            }}
                          >
                            👁 {user.userId}
                          </a>
                        </td>
                        <td className="p-2 border whitespace-nowrap">
                          <a
                            href="#"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                            onClick={function (e) {
                              e.preventDefault();
                              handleAction('downline', user);
                            }}
                          >
                            {user.fullName || user.username}
                          </a>
                        </td>
                        <td className="p-2 border">
                          {user.parentId?.username || '-'}
                          {user.parentId?.userId && (
                            <span className="text-xs text-gray-500 ml-2">
                              ({user.parentId.userId})
                            </span>
                          )}
                        </td>
                        <td className="p-2 border">****</td>
                        <td className="p-2 border">{user.shares?.myShare || 0}</td>
                        <td className="p-2 border text-center">{user.shares?.matchCommission || 0}</td>
                        <td className="p-2 border text-center">{user.shares?.sessionCommission || 0}</td>
                        <td className="p-2 border text-center">{user.shares?.casinoCommission || 0}</td>
                        <td className="p-2 border text-center">{user.shares?.matkaCommission || 0}</td>
                        <td className="p-2 border">{(user.exposureLimit || 0).toFixed(2)}</td>
                        <td className="p-2 border">
                          <span className="text-red-600 font-semibold">Inactive</span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Dropdown Menu */}
        {activeDropdown && (
          <ul
            className="dropdown-menu absolute bg-white rounded shadow-lg z-50 py-1 min-w-[180px] list-none m-0 p-0"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
            }}
          >
            <li
              onClick={function(e) {
                e.stopPropagation();
                const user = users.find(function(u) { return u.userId === activeDropdown; });
                if (user) handleAction('toggleStatus', user);
              }}
              className="px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-100 text-gray-700"
              style={{ lineHeight: '22px' }}
            >
              <span>Active</span>
            </li>
            <li
              onClick={function(e) {
                e.stopPropagation();
                const user = users.find(function(u) { return u.userId === activeDropdown; });
                if (user) handleAction('statement', user);
              }}
              className="px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-100 font-semibold text-gray-800"
              style={{ lineHeight: '22px' }}
            >
              <span>Statement</span>
            </li>
            <li
              onClick={function(e) {
                e.stopPropagation();
                const user = users.find(function(u) { return u.userId === activeDropdown; });
                if (user) handleAction('accountOps', user);
              }}
              className="px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-100 font-semibold text-gray-800"
              style={{ lineHeight: '22px' }}
            >
              <span>Account Operations</span>
            </li>
            <li
              onClick={function(e) {
                e.stopPropagation();
                const user = users.find(function(u) { return u.userId === activeDropdown; });
                if (user) handleAction('loginReport', user);
              }}
              className="px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-100 font-semibold text-gray-800"
              style={{ lineHeight: '22px' }}
            >
              <span>Login Report</span>
            </li>
            <li
              onClick={function(e) {
                e.stopPropagation();
                const user = users.find(function(u) { return u.userId === activeDropdown; });
                if (user) handleAction('resetPassword', user);
              }}
              className="px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-100 text-gray-700"
              style={{ lineHeight: '22px' }}
            >
              <span>Reset Password</span>
            </li>
            <li
              onClick={function(e) {
                e.stopPropagation();
                const user = users.find(function(u) { return u.userId === activeDropdown; });
                if (user) handleAction('downline', user);
              }}
              className="px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-100 text-gray-700"
              style={{ lineHeight: '22px' }}
            >
              <span>Downline</span>
            </li>
          </ul>
        )}

        {/* Partnership Modal */}
        <PartnershipModal
          isOpen={partnershipModal.isOpen}
          onClose={handleClosePartnership}
          userId={formatUserId(partnershipModal.userId)}
          parentShare={parentShare}
          loading={loading.parentShare}
        />

        {/* Password Modal */}
        <PasswordModal
          isOpen={passwordModal.isOpen}
          onClose={function () {
            setPasswordModal({ isOpen: false, user: null });
          }}
          user={passwordModal.user}
        />
      </div>
    </div>
  );
};

export default DeadClientPage;